/**
 * Cell keys, message keys and the audit shape the audit summary panel
 * reads.
 *
 * The strings are declared once here as const maps and both union types
 * are derived from them, so a key cannot drift between its type and its
 * use in `auditSummaryCells`.
 */

export const AUDIT_SUMMARY_BUNDLE = 'features.audit.translations.auditDetailSummary';

/** Identifies each cell the summary panel can render. */
export const AUDIT_SUMMARY_FIELD = {
  auditId: 'auditId',
  status: 'status',
  score: 'score',
  location: 'location',
  businessUnit: 'businessUnit',
  division: 'division',
  auditType: 'auditType',
  templateId: 'templateId',
  template: 'template',
  assignees: 'assignees',
  scheduledDate: 'scheduledDate',
  startDate: 'startDate',
  expiryDate: 'expiryDate',
  completed: 'completed',
  completedBy: 'completedBy',
  createdBy: 'createdBy',
  reopenedBy: 'reopenedBy',
  reopenedReason: 'reopenedReason',
  reopenedAt: 'reopenedAt',
} as const;

/**
 * Every cell the summary panel can render. Callers pass a subset to
 * `selectAuditSummaryCells` to restrict and reorder the panel.
 */
export type AuditSummaryFieldKey =
  (typeof AUDIT_SUMMARY_FIELD)[keyof typeof AUDIT_SUMMARY_FIELD];

/** Resource names in the auditDetailSummary bundle. */
export const AUDIT_SUMMARY_MESSAGE = {
  summaryTitle: 'summaryTitle',
  unassigned: 'unassigned',
  labelAuditId: 'labelAuditId',
  labelStatus: 'labelStatus',
  labelScore: 'labelScore',
  labelLocation: 'labelLocation',
  labelBusinessUnit: 'labelBusinessUnit',
  labelDivision: 'labelDivision',
  labelAuditType: 'labelAuditType',
  labelTemplateId: 'labelTemplateId',
  labelTemplate: 'labelTemplate',
  labelAssignedTo: 'labelAssignedTo',
  labelScheduledDate: 'labelScheduledDate',
  labelStartDate: 'labelStartDate',
  labelExpiryDate: 'labelExpiryDate',
  labelCompleted: 'labelCompleted',
  labelCompletedBy: 'labelCompletedBy',
  labelCreatedBy: 'labelCreatedBy',
  labelReopenedBy: 'labelReopenedBy',
  labelReopenedReason: 'labelReopenedReason',
  labelReopenedAt: 'labelReopenedAt',
} as const;

export type AuditSummaryMessageKey =
  (typeof AUDIT_SUMMARY_MESSAGE)[keyof typeof AUDIT_SUMMARY_MESSAGE];

/**
 * Translation lookup passed to the cell builder. Matches the shape of
 * `useMessages().t` so the builder stays a pure helper without importing
 * the hook.
 */
export type AuditSummaryTranslator = (key: AuditSummaryMessageKey) => string;

/**
 * The audit fields the summary panel reads.
 *
 * Every name here is one the generated client's `Audit` declares, but this is not a
 * `Pick` of it. Two callers feed the panel and their audits are different shapes: mobile
 * passes the client's `Audit`, where the dates are `Date`; the desktop summary page passes
 * `src/types/helios/Audit`, where the same dates are epoch `number`. This is the contract
 * both satisfy, so each date admits either spelling.
 *
 * `status` and `auditType` stay `string` for that reason too, and because the panel renders
 * a status this build has not learned rather than dropping the row — see the neutral-badge
 * fallback in `deriveAuditSummaryValues`.
 *
 * Optional members carry an explicit `| undefined` so the client's `Audit` stays assignable
 * under exactOptionalPropertyTypes.
 */
export interface AuditSummaryAudit {
  auditId: string;
  status: string;
  location: string;
  auditType: string;
  businessUnit?: string | null | undefined;
  divisionName?: string | null | undefined;
  templateId?: string | null | undefined;
  templateName?: string | null | undefined;
  assignees?: string[] | null | undefined;
  scheduledDate?: Date | string | number | null | undefined;
  startDate?: Date | string | number | null | undefined;
  expirationDate?: Date | string | number | null | undefined;
  completionDate?: Date | string | number | null | undefined;
  completedBy?: string | null | undefined;
  createdBy?: string | null | undefined;
  reopenedBy?: string | null | undefined;
  reopenedReason?: string | null | undefined;
  reopenedDate?: Date | string | number | null | undefined;
  metrics?: { scorePercent?: number | null | undefined } | null | undefined;
}
